import { ChangeDetectorRef, Component, ElementRef, HostBinding, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { merge, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AppConfigService, LoggingService, SessionService, SessionUser, USER_TYPE_SHOWROOM_GUEST } from "src/app/core";
import { BrandingService } from "src/app/core/services/branding.service";
import { Showroom } from "src/app/features/showrooms/models";
import { ShowroomService } from "src/app/features/showrooms/services";
import { BannerComponentConfig } from "src/app/shared";
import { HeaderService, LayoutService } from "../services";
import { HeaderBannerService } from "./services/header-banner.service";

const Workflow = "Header";
const WorkflowSource = "Header";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  @ViewChild("header", { static: true }) headerEl: ElementRef;

  @ViewChild("drawerContainer") set drawerContainer(drawerContainerEl: ElementRef<HTMLElement> | undefined) {
    if (drawerContainerEl && window.ResizeObserver) {
      // A drawer opened: stop tracking any previous (now removed) drawer and observe this one so
      // its height - including the open/close animation - is reported as the drawer's height.
      this.drawerResizeObserver?.disconnect();

      this.drawerResizeObserver = new ResizeObserver(entries => {
        this._headerService.setDrawerHeight(entries[0].contentRect.height);
      });

      this.drawerResizeObserver.observe(drawerContainerEl.nativeElement);
    }

    // When drawerContainerEl is undefined the element is animating out via `animate.leave`; keep
    // the existing observer attached so it keeps reporting the shrinking height down to ~0 instead
    // of snapping the reserved space away at the start of the collapse.
  }

  @HostBinding("class.sticky") stickyClass: boolean;

  user: SessionUser;
  showroom: Showroom;
  loggedOut: boolean;
  sidePadding: boolean;
  bannerConfigs: BannerComponentConfig[];
  hasBannerConfigs: boolean;
  resizeObserver: ResizeObserver;
  drawerResizeObserver: ResizeObserver;
  intersectionObserver: IntersectionObserver;
  hideHeader: boolean;
  hideBanner: boolean;
  hideUpgradeButton: boolean;
  homeUrl: string;
  headerDrawer: TemplateRef<any>;

  constructor(
    private sessionService: SessionService,
    private layoutService: LayoutService,
    private _headerBannerService: HeaderBannerService,
    private _headerService: HeaderService,
    private _showroomService: ShowroomService,
    private _cdr: ChangeDetectorRef,
    private _appConfigService: AppConfigService,
    private _hostEl: ElementRef<HTMLElement>,
    private _loggingService: LoggingService,
    public brandingService: BrandingService,
  ) {}

  ngOnInit(): void {
    this.homeUrl = this._appConfigService.homeUrl;

    this.layoutService.layoutChange$.pipe(takeUntil(this._destroyed$)).subscribe(layout => {
      this.sidePadding = layout.headerSidePadding;
      this.hideHeader = layout.hideHeader;
      this.hideBanner = layout.hideHeaderBanner;
      this.hideUpgradeButton = layout.hideUpgradeButton;
      this._hostEl.nativeElement.style.setProperty("--titleImageHeight", "32px");
    });

    merge(this.sessionService.sessionUser$, this._showroomService.loadedShowroom$)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({ next: () => this._setState() });

    this._headerBannerService.configs$.pipe(takeUntil(this._destroyed$)).subscribe(configs => {
      this.bannerConfigs = configs;
      this.hasBannerConfigs = this.bannerConfigs?.length > 0;
      this._cdr.detectChanges();
    });

    this._headerService.isStickyHeader$.pipe(debounceTime(50), takeUntil(this._destroyed$)).subscribe({
      next: (isSticky: boolean) => {
        this.stickyClass = isSticky;
      },
    });

    this._headerService.headerDrawer$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: (headerDrawer: TemplateRef<any>) => {
        this.headerDrawer = headerDrawer;
      },
    });

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(entries => {
        const newHeaderSize = entries[0].contentRect.height;
        this._headerService.setHeaderHeight(newHeaderSize);
      });

      this.resizeObserver.observe(this.headerEl.nativeElement);
    }

    if (window.IntersectionObserver) {
      // This intersection observer helps tell us if the header is visible and when its scrolled
      // off the top of the screen
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          this._headerService.setIsHeaderVisible(entries[0].isIntersecting);
        },
        {
          threshold: 0,
        },
      );

      this.intersectionObserver.observe(this.headerEl.nativeElement);
    }

    this._headerService.setHeaderElementRef(this.headerEl);
  }

  onLogoClick(): void {
    this._loggingService.logWorkflow("Merri Logo Click", "HeaderComponent.onLogoClick", Workflow, "LogoClick", WorkflowSource);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.resizeObserver?.unobserve(this.headerEl.nativeElement);
    this.drawerResizeObserver?.disconnect();
    this.intersectionObserver?.unobserve(this.headerEl.nativeElement);
  }

  private _setState(): void {
    this.user = this.sessionService.getSessionUser();
    this.loggedOut = !this.user;

    const showroom = this._showroomService.currentShowroom();
    this.showroom = this.sessionService.getUserType() === USER_TYPE_SHOWROOM_GUEST && showroom;
  }
}
